import { base, type IIconProps } from './base';

/* ---------- SLS Site Map glyphs ----------
   Area-icon and equipment-catalog markers for the site-map panel (spec §5,
   §8). Stroke-only via the shared `base` props at a slightly heavier weight
   (~1.7) so the small map markers stay legible at 16–24px. All variants pick
   up colour from the wrapping `currentColor`. */

const stroke = { ...base, strokeWidth: 1.7 };

/** Three stacked layers — the toolbar's layers-menu trigger (show/hide areas
 *  and radii). Moved here from `SiteMap.tsx` (was a single-use local
 *  component) once that file needed the headroom for the fullscreen-expand
 *  feature. */
export function LayersIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
      <path d="m6.08 9.66-3.5 1.6a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83l-3.5-1.61" />
      <path d="m6.08 14.66-3.5 1.6a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83l-3.5-1.61" />
    </svg>
  );
}

/** Stacked-boards pallet + crate — the "pallet / staging" area icon. */
export function PalletIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <rect x="5" y="4" width="14" height="10" rx="1" />
      <line x1="5" y1="9" x2="19" y2="9" />
      <line x1="10" y1="4" x2="10" y2="14" />
      <line x1="3" y1="18" x2="21" y2="18" />
      <line x1="3" y1="21" x2="21" y2="21" />
      <line x1="5" y1="18" x2="5" y2="21" />
      <line x1="12" y1="18" x2="12" y2="21" />
      <line x1="19" y1="18" x2="19" y2="21" />
    </svg>
  );
}

/** Fuel droplet — the "fuel" area icon. */
export function FuelDropIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M12 2.7c3 3.6 6 7.3 6 10.8a6 6 0 1 1-12 0c0-3.5 3-7.2 6-10.8z" />
    </svg>
  );
}

/** Traffic cone — the "keep clear" area icon. */
export function KeepClearIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M12 3l5 15H7z" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="10" y1="8.5" x2="14" y2="8.5" />
      <line x1="4" y1="21" x2="20" y2="21" />
    </svg>
  );
}

/** Tower crane — mast + jib + counter-jib. */
export function TowerCraneIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <line x1="7" y1="21" x2="7" y2="4" />
      <line x1="2" y1="4" x2="22" y2="4" />
      <line x1="7" y1="4" x2="4" y2="8" />
      <line x1="7" y1="4" x2="19" y2="8" />
      <line x1="19" y1="4" x2="19" y2="8" />
      <line x1="3" y1="21" x2="11" y2="21" />
    </svg>
  );
}

/** Mobile crane — truck body + diagonal boom. */
export function MobileCraneIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <rect x="2" y="13" width="10" height="6" rx="1" />
      <line x1="6" y1="13" x2="19" y2="3" />
      <circle cx="5" cy="19.5" r="1.5" />
      <circle cx="11" cy="19.5" r="1.5" />
    </svg>
  );
}

/** Excavator — tracked base + cab + boom/bucket. */
export function ExcavatorIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <rect x="2" y="17" width="16" height="3" rx="1" />
      <rect x="6" y="10" width="8" height="6" rx="1" />
      <line x1="12" y1="10" x2="18" y2="5" />
      <line x1="18" y1="5" x2="21" y2="8" />
      <line x1="21" y1="8" x2="18" y2="9.5" />
    </svg>
  );
}

/** Scissor lift — the zig-zag scissor mechanism + platform. */
export function ScissorLiftIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <line x1="4" y1="5" x2="4" y2="7" />
      <line x1="20" y1="5" x2="20" y2="7" />
      <polyline points="4 7 20 15 4 21" />
      <polyline points="20 7 4 15 20 21" />
      <line x1="2" y1="21" x2="22" y2="21" />
    </svg>
  );
}

/** Concrete pump — truck body + folded boom segments. */
export function ConcretePumpIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <rect x="2" y="14" width="9" height="5" rx="1" />
      <circle cx="4.5" cy="19.5" r="1.5" />
      <circle cx="9.5" cy="19.5" r="1.5" />
      <line x1="7" y1="14" x2="7" y2="9" />
      <line x1="7" y1="9" x2="15" y2="6" />
      <line x1="15" y1="6" x2="17" y2="11" />
    </svg>
  );
}

/** Generator — housing box + exhaust flash. */
export function GeneratorIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <rect x="3" y="9" width="14" height="10" rx="1.5" />
      <line x1="6" y1="13" x2="14" y2="13" />
      <line x1="6" y1="16" x2="14" y2="16" />
      <path d="M19 11l-3 4h2.5l-2 4.5" />
    </svg>
  );
}

/** Diagonal-arrows-outward glyph — the site-map panel's fullscreen-expand
 *  toolbar button (paired with `CollapseIcon` below, swapped in once
 *  expanded). Two opposite corner brackets (top-right, bottom-left) each with
 *  a diagonal stroke pointing away from centre. */
export function ExpandIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" />
    </svg>
  );
}

/** `ExpandIcon`'s collapse counterpart — the same two corner brackets with
 *  their diagonal strokes reversed to point back toward centre. */
export function CollapseIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M20 10h-6V4M14 10l7-7M4 14h6v6M10 14l-7 7" />
    </svg>
  );
}

/** The 3-vertical-dots kebab glyph — the settings / "more" affordance. Call
 *  sites use it through `ui/KebabButton`, never bare. Filled, not stroked,
 *  unlike the rest of this file's markers. */
export function KebabIcon({ size = 16 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
  );
}
